import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, Loader2, Plus } from "lucide-react";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import ImageUploader from "../ImageUploader";
import { projectsAPI } from "@/api/projects";
import { peopleAPI } from "@/api/people";
import CurrencyDropdown from '@/components/common/CurrencyDropdown';
import { getCurrencyAmountLabel } from "@/utils/currencyUtils";
import { useUserPreferences } from '@/contexts/UserPreferencesContext';

interface LaborCostFormProps {
  date: Date;
  projectId: string;
  personId: string;
  description: string;
  amount: string;
  imageFile: File | null;
  imagePreview: string | null;
  onDateChange: (date: Date) => void;
  onProjectChange: (projectId: string) => void;
  onPersonChange: (personId: string) => void;
  onDescriptionChange: (description: string) => void;
  onAmountChange: (amount: string) => void;
  onImageChange: (file: File) => void;
  onSubmit: (e: React.FormEvent) => void;
  currency?: string;
  onCurrencyChange?: (currency: string) => void;
}

const LaborCostForm: React.FC<LaborCostFormProps> = ({
  date,
  projectId,
  personId,
  description,
  amount,
  imageFile,
  imagePreview,
  onDateChange,
  onProjectChange,
  onPersonChange,
  onDescriptionChange,
  onAmountChange,
  onImageChange,
  onSubmit,
  currency,
  onCurrencyChange = () => {},
}) => {
  const { preferences } = useUserPreferences();
  const [people, setPeople] = useState<{ id: string; name: string }[]>([]);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [isLoadingPeople, setIsLoadingPeople] = useState(true);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [selectedCurrency, setSelectedCurrency] = useState(currency || preferences?.base_currency || 'TRY');

  // Set the initial currency from user preferences if not already set
  useEffect(() => {
    if (!currency && preferences?.base_currency && selectedCurrency !== preferences.base_currency) {
      setSelectedCurrency(preferences.base_currency);
      onCurrencyChange(preferences.base_currency);
    }
  }, [preferences, currency, onCurrencyChange, selectedCurrency]);

  // Fetch projects from the API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        
        setIsLoadingProjects(true);
        const data = await projectsAPI.getProjects();
        // Map the data to simplified format for the dropdown
        const mappedProjects = data.map(project => ({
          id: project.id,
          name: project.name
        }));
        
        if (mappedProjects.length === 0) {
          
        }
        setProjects(mappedProjects);
      } catch (error) {
        
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjects();
  }, []);

  // Fetch people from the API
  useEffect(() => {
    const fetchPeople = async () => {
      try {
        
        setIsLoadingPeople(true);
        const data = await peopleAPI.getPeople();
        // Map the data to simplified format for the dropdown
        const mappedPeople = data.map(person => ({
          id: person.id,
          name: person.name
        }));
        
        if (mappedPeople.length === 0) {
          
        }
        setPeople(mappedPeople);
      } catch (error) {
        
      } finally {
        setIsLoadingPeople(false);
      }
    };

    fetchPeople();
    
    // Add listener for people data refresh events
    const handlePeopleDataRefresh = (event: any) => {
      
      fetchPeople();
    };
    
    window.addEventListener('people-data-refresh', handlePeopleDataRefresh);
    
    return () => {
      window.removeEventListener('people-data-refresh', handlePeopleDataRefresh);
    };
  }, []);

  const isFormValid = () => {
    const valid = !!date && !!projectId && !!personId && 
      description.trim() !== '' && amount.trim() !== '';
    
    if (!valid) {
      
    }
    
    return valid;
  };

  // Add logging to input handlers
  const handleProjectChange = (value: string) => {
    
    onProjectChange(value);
  };

  const handlePersonChange = (value: string) => {
    
    onPersonChange(value);
  };

  const handleAmountChange = (value: string) => {
    
    onAmountChange(value);
  };

  const handleCurrencyChange = (value: string) => {
    setSelectedCurrency(value);
    onCurrencyChange(value);
  };

  const handleSubmitWithLogging = (e: React.FormEvent) => {
    
    
    
    onSubmit(e);
  };

  return (
    <form onSubmit={handleSubmitWithLogging} className="space-y-4 pb-6 pt-4 px-4">
      {/* Official record switch row */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-xs">
        <div>
          <p className="text-[13px] font-semibold text-gray-900">Resmi Kayıt</p>
          <p className="text-[11px] text-gray-500">Fatura / resmi belge mevcut</p>
        </div>
        <label className="relative inline-flex cursor-pointer items-center">
          <input type="checkbox" className="sr-only peer" />
          <div className="h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-brand-600 peer-checked:after:translate-x-4" />
        </label>
      </div>

      {/* Date */}
      <div className="space-y-1.5">
        <label htmlFor="date" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Tarih
        </label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-mono text-[13px] border-gray-200 bg-white hover:bg-gray-50 rounded-md h-10",
                !date && "text-gray-400",
              )}
            >
              <Calendar className="mr-2 h-4 w-4 text-gray-400 font-sans" />
              {date ? format(date, "dd.MM.yyyy") : "gg/aa/yyyy"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <CalendarComponent
              mode="single"
              selected={date}
              onSelect={(date) => date && onDateChange(date)}
              initialFocus
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Project */}
      <div className="space-y-1.5">
        <label htmlFor="project" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Proje
        </label>
        <Select
          value={projectId}
          onValueChange={handleProjectChange}
        >
          <SelectTrigger className="w-full border-gray-200 bg-white rounded-md h-10 text-[13px] text-gray-900 focus:border-brand-600 focus:ring-1 focus:ring-brand-300">
            {isLoadingProjects ? (
              <div className="flex items-center text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span className="text-[13px]">Yükleniyor...</span>
              </div>
            ) : (
              <SelectValue placeholder="Proje seçin" />
            )}
          </SelectTrigger>
          <SelectContent>
            {projects.map(project => (
              <SelectItem key={project.id} value={project.id}>
                {project.name}
              </SelectItem>
            ))}
            {projects.length === 0 && !isLoadingProjects && (
              <div className="py-2 px-4 text-[13px] text-gray-500">
                Proje bulunamadı
              </div>
            )}
          </SelectContent>
        </Select>
      </div>

      {/* Person */}
      <div className="space-y-1.5">
        <label htmlFor="person" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Anlaşılan Kişi
        </label>
        <Select
          value={personId}
          onValueChange={handlePersonChange}
        >
          <SelectTrigger className="w-full border-gray-200 bg-white rounded-md h-10 text-[13px] text-gray-900 focus:border-brand-600 focus:ring-1 focus:ring-brand-300">
            {isLoadingPeople ? (
              <div className="flex items-center text-gray-500">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                <span className="text-[13px]">Yükleniyor...</span>
              </div>
            ) : (
              <SelectValue placeholder="Kişi seçin" />
            )}
          </SelectTrigger>
          <SelectContent>
            {people.map(person => (
              <SelectItem key={person.id} value={person.id}>
                {person.name}
              </SelectItem>
            ))}
            {people.length === 0 && !isLoadingPeople && (
              <div className="py-2 px-4 text-[13px] text-gray-500">
                Kişi bulunamadı
              </div>
            )}
            <SelectItem value="new" className="text-brand-600 font-semibold">
              <span className="flex items-center">
                <Plus className="mr-1 h-3 w-3" />
                + Yeni kişi
              </span>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Amount with currency prefix */}
      <div className="space-y-1.5">
        <label htmlFor="amount" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          {getCurrencyAmountLabel(selectedCurrency)}
        </label>
        <div className="flex items-center rounded-md border border-gray-200 bg-white focus-within:border-brand-600 focus-within:ring-1 focus-within:ring-brand-300 overflow-hidden">
          <span className="flex-shrink-0 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-500 border-r border-gray-200 bg-gray-50 select-none">
            {selectedCurrency}
          </span>
          <input
            id="amount"
            type="number"
            step="0.01"
            min="0"
            placeholder="0.00"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            className="flex-1 px-3 py-2 text-[13px] font-mono tabular-nums text-gray-900 bg-transparent outline-none placeholder:text-gray-400"
          />
          <div className="pr-2">
            <CurrencyDropdown
              value={selectedCurrency}
              onValueChange={handleCurrencyChange}
            />
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="space-y-1.5">
        <label htmlFor="description" className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Açıklama
        </label>
        <input
          id="description"
          placeholder="Açıklama"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          className="flex w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-brand-600 focus:ring-1 focus:ring-brand-300"
        />
      </div>

      {/* Image */}
      <div className="space-y-1.5">
        <label className="block text-[11px] font-semibold uppercase tracking-[0.04em] text-gray-600">
          Görsel
        </label>
        <ImageUploader
          imageFile={imageFile}
          imagePreview={imagePreview}
          onImageChange={onImageChange}
        />
      </div>

      {/* Footer: Vazgeç + Kaydet */}
      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
          onClick={() => {}}
        >
          Vazgeç
        </Button>
        <Button
          type="submit"
          className="flex-1 bg-brand-600 hover:bg-brand-700 active:bg-brand-800 text-white"
          disabled={!isFormValid()}
        >
          Kaydet
        </Button>
      </div>
    </form>
  );
};

export default LaborCostForm;
